import { Div, Icon, Label, Loader, Touchable, useWindowSize } from "@grassrootly/rn-core";
import { ScreenLayout } from "../../layout/components";
import { MainStackScreenProps } from "../../navigation/Navigation.types";
import GamePreviewInfo from "../../components/game/GamePreviewInfo";
import { useEditGame } from "./useEditGame";
import GameStatuses from "../../components/game/game-statuses/GameStatuses";
import { GameStatues, GameTeams, IGame } from "@grassrootly/api";
import { useEffect, useMemo, useState } from "react";
import EditGameAtendees from "../../components/game/game-attendees/EditGameAttendees";
import EditGameDetails from "../../components/game/game-details/EditGameDetails";
import EditGameStats from "../../components/game/game-stats/EditGameStats";
import EditGameLineups from "../../components/game/game-lineups/EditGameLineups";
import GameMVP from "../../components/game/game-mvp/GameMVP";
import { getGameMVP } from "../../components/game/lib";
import { GameType } from "../new-game/NewGame.types";
import { ScrollView } from "react-native";

export default function EditGameScreen({ navigation, route }: MainStackScreenProps<"edit-game">) {
  const {
    game,
    loadingGame,
    setAttendance,
    goBack,
    changeStatus,
    changeStatusInProgress,
    setPlayersStats,
    playersStatsUpdateInPorgress,
    updateGameDetails,
    updateGameInProgress,
    updateGameScore,
    availableStatuses,
  } = useEditGame({ navigation, route });

  // Index for selected tab/section
  const [index, setIndex] = useState(0);
  const { isMobile } = useWindowSize();

  // List of available tabs/sections based on the game status
  const sections = useMemo(() => {
    if (!!game && [GameStatues.FINISHED, GameStatues.FINALIZED].indexOf(game.status) === -1) {
      const baseSections = [
        {
          key: "attendees",
          title: "ATTENDEES",
          renderComponent: () => (
            <EditGameAtendees
              key={0}
              attendees={game.attendees}
              setAttendance={setAttendance}
              game={game}
              navigation={navigation}
            />
          ),
        },
      ];

      // For TRAINING games, add separate LINEUPS and SCORE/STATS tabs
      if (game.game_type === GameType.TRAINING) {
        baseSections.push({
          key: "lineups",
          title: "LINEUPS",
          renderComponent: () => (
            <EditGameLineups
              key={1}
              attendees={game.attendees}
              setAttendance={setAttendance}
              game={game}
              navigation={navigation}
            />
          ),
        });

        // Add SCORE/STATS tab only for CONFIRMED status
        if (game.status === GameStatues.CONFIRMED) {
          baseSections.push({
            key: "stats",
            title: "SCORES",
            renderComponent: () => (
              <EditGameStats
                gameStatus={game.status}
                key={2}
                attendees={game.attendees}
                game={game}
                updateGameScore={updateGameScore}
                setPlayerStats={setPlayersStats}
                updateDisabled={false}
                goals={[
                  { team: GameTeams.hosts.key, goals: game.host_score || 0 },
                  { team: GameTeams.visitors.key, goals: game.visitor_score || 0 },
                ]}
              />
            ),
          });
        }
      } else {
        // For COMPETITIVE games, use the original logic
        baseSections.push({
          key: "stats",
          title: game.status === GameStatues.CONFIRMED ? "SCORES" : "LINEUPS",
          renderComponent: () => (
            <EditGameStats
              gameStatus={game.status}
              key={2}
              attendees={game.attendees}
              game={game}
              updateGameScore={updateGameScore}
              setPlayerStats={setPlayersStats}
              updateDisabled={game.status !== GameStatues.CONFIRMED}
              goals={[
                { team: GameTeams.hosts.key, goals: game.host_score || 0 },
                { team: GameTeams.visitors.key, goals: game.visitor_score || 0 },
              ]}
            />
          ),
        });
      }

      baseSections.push({
        key: "details",
        title: "DETAILS",
        renderComponent: () => (
          <EditGameDetails
            key={3}
            game={game}
            updateGameDetails={updateGameDetails}
            updateGameInProgress={updateGameInProgress}
          />
        ),
      });

      return baseSections;
    }
    if (!!game && game.status === GameStatues.FINISHED) {
      // For FINISHED games, show SCORES and DETAILS tabs
      // Game result (score) should not be editable when FINISHED, but player stats should be
      return [
        {
          key: "stats",
          title: "SCORES",
          renderComponent: () => (
            <EditGameStats
              key={2}
              gameStatus={game.status}
              game={game}
              attendees={game.attendees}
              updateGameScore={updateGameScore}
              setPlayerStats={setPlayersStats}
              updateDisabled={false}
              disableScoreEdit={true}
              goals={[
                { team: GameTeams.hosts.key, goals: game.host_score || 0 },
                { team: GameTeams.visitors.key, goals: game.visitor_score || 0 },
              ]}
            />
          ),
        },
        {
          key: "details",
          title: "DETAILS",
          renderComponent: () => (
            <EditGameDetails
              key={1}
              game={game}
              updateGameDetails={updateGameDetails}
              updateGameInProgress={updateGameInProgress}
            />
          ),
        },
      ];
    }
    if (!!game && game.status === GameStatues.FINALIZED) {
      // For FINALIZED games, show only DETAILS tab
      return [
        {
          key: "details",
          title: "DETAILS",
          renderComponent: () => (
            <EditGameDetails
              key={1}
              game={game}
              updateGameDetails={updateGameDetails}
              updateGameInProgress={updateGameInProgress}
            />
          ),
        },
      ];
    }
    return [];
  }, [game, setPlayersStats, playersStatsUpdateInPorgress]);

  // Auto-select first tab when there's only one tab or when sections change
  useEffect(() => {
    if (sections.length === 1) {
      setIndex(0);
    } else if (index >= sections.length) {
      setIndex(0);
    }
  }, [sections, index]);

  return (
    <ScreenLayout
      header={{
        center: `EDIT GAME`,
        left: <Icon name="arrow_back_ios" size={24} onPress={goBack} classNames={["mb-{5}"]} />,
      }}
      centerContent={false}
    >
      <ScrollView 
        style={{ flex: 1, backgroundColor: '#072331' }}
        contentContainerStyle={{ minHeight: '100%', backgroundColor: '#072331' }}
        showsVerticalScrollIndicator={false}
      >
        {!game && (
          <Div classNames={["flex1", "alignContentCenter", "mt-{30}"]}>
            {loadingGame ? (
              <Loader size="large" isLoading />
            ) : (
              <Label type="l1Bold" classNames={["textCenter", "colorSecondaryLight"]}>
                Game not found
              </Label>
            )}
          </Div>
        )}

        {game && (
          <>
            {/* Game basic information */}
            <GamePreviewInfo game={game} hideNotes={isMobile} simpleView={isMobile} />

            {/* Game status label/selector */}
            <GameStatuses
              game={game}
              onStatusChanged={(newStatus: string, game: IGame) => changeStatus({ newStatus: newStatus as GameStatues })}
              availableStatuses={availableStatuses}
              changeStatusInProgress={changeStatusInProgress}
            />

            {/* Game MVP section */}
            <Div classNames={["mt-{16}"]}>
              {game.status === GameStatues.FINALIZED && game.attendees.length && (
                <GameMVP mvp={getGameMVP(game.attendees)} navigation={navigation} />
              )}
            </Div>

            {/* Game tab/sections */}
            <Div classNames={["bgNeutralDarkerDark", "minH-{100vh}"]}>
              <Div classNames={["flexRow", "bgNeutralDarkerDark"]}>
                {sections.map((section, i) =>
                  section.key === "stats" &&
                  game.status === GameStatues.SCHEDULED &&
                  game.game_type === GameType.COMPETITIVE ? null : (
                    <Touchable
                      classNames={[
                        "flex1",
                        "alignItemsCenter",
                        "justifyContentCenter",
                        "pv16",
                        "bwBottom-{1}",
                        i === index ? "borderPrimary" : "borderSecondary",
                        "mb-{8}",
                      ]}
                      key={section.key}
                      onPress={() => setIndex(i)}
                    >
                      <Label>{section.title}</Label>
                    </Touchable>
                  )
                )}
              </Div>
              <Div classNames={["overflowVisible", "bgNeutralDarkerDark", "minH-{100vh}"]}>
                {sections.map((section, i) => (i === index ? section.renderComponent() : null))}
              </Div>
            </Div>
          </>
        )}
      </ScrollView>
    </ScreenLayout>
  );
}
